// Display helpers shared across the app.

export function fmtMoney(cents, currency = "AUD") {
  if (cents == null) return "";
  const v = (cents / 100).toFixed(2).replace(/\.00$/, "");
  return `A$${v}`;
}

export function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

const SPECIALTY = {
  "Medicine": { accent: "#0F766E", soft: "#D7F3EF" },
  "Surgery": { accent: "#0EA5E9", soft: "#D9F0FF" },
  "Paediatrics": { accent: "#16A34A", soft: "#DCFCE7" },
  "Psychiatry": { accent: "#B45309", soft: "#FDE7C7" },
  "Obstetrics & Gynaecology": { accent: "#E11D48", soft: "#FFE4E6" },
  "Emergency Medicine": { accent: "#DC2626", soft: "#FEE2E2" },
};
const DEFAULT_SPECIALTY = { accent: "#0F766E", soft: "#D7F3EF" };

export function specialtyStyle(specialty) {
  if (!specialty) return DEFAULT_SPECIALTY;
  return SPECIALTY[specialty] || DEFAULT_SPECIALTY;
}

export function difficultyStyle(difficulty) {
  switch ((difficulty || "").toLowerCase()) {
    case "easy": return "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]";
    case "hard": return "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]";
    default: return "bg-[#FDE7C7] text-[#92400E] border-[#FBBF24]";
  }
}

export function resultStyle(result) {
  switch ((result || "").toLowerCase()) {
    case "pass": return "bg-[#DCFCE7] text-[#166534] border border-[#86EFAC]";
    case "fail": return "bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5]";
    default: return "bg-[#FDE7C7] text-[#92400E] border border-[#FBBF24]";
  }
}

export function scoreColor(score) {
  if (score == null) return "#64748B";
  if (score >= 65) return "#16A34A";
  if (score >= 50) return "#B45309";
  return "#DC2626";
}

// --- AMC 1-7 global scale (pass mark = 4) ---
export function isSevenPoint(feedback) {
  return feedback?.scale_max === 7;
}

export function scoreColor7(score) {
  if (score == null) return "#64748B";
  if (score >= 4) return "#16A34A"; // pass
  if (score === 3) return "#B45309"; // borderline
  return "#DC2626"; // fail
}

// Normalise any session score to a 0-100 percentage regardless of scale (for averages/summaries).
export function scoreToPercent(score, feedback) {
  if (score == null) return 0;
  return isSevenPoint(feedback) ? Math.round((score / 7) * 100) : Math.round(score);
}

// Short label for a session score badge, e.g. "5/7" (new) or "72/100" (legacy).
export function scoreLabel(score, feedback) {
  if (score == null) return "—";
  return isSevenPoint(feedback) ? `${score}/7` : `${score}/100`;
}

export const DOMAIN_LABELS = {
  communication: "Communication",
  history_taking: "History Taking",
  empathy_professionalism: "Empathy & Professionalism",
  clinical_reasoning: "Clinical Reasoning",
  organisation: "Organisation",
  patient_centred: "Patient-Centred Care",
  safety_netting: "Safety-Netting",
};

// AMC 10-domain marking scheme used by the Proactive Station feedback report.
// Legacy 7-domain keys are kept in DOMAIN_LABELS above so old sessions still render.
export const AMC_DOMAIN_LABELS = {
  communication: "Communication",
  history_taking: "History Taking",
  physical_examination: "Physical Examination",
  clinical_reasoning: "Clinical Reasoning",
  diagnostic_formulation: "Diagnostic Formulation",
  management: "Management",
  counselling: "Counselling",
  professionalism: "Professionalism",
  organisation: "Organisation",
  patient_safety: "Patient Safety",
};

// The domains a station's predominant assessment area maps onto (for visual emphasis).
export const PREDOMINANT_DOMAIN_MAP = {
  history_taking: ["history_taking"],
  physical_examination: ["physical_examination"],
  diagnostic_formulation: ["diagnostic_formulation", "clinical_reasoning"],
  management_counselling: ["management", "counselling"],
};

export const PREDOMINANT_LABELS = {
  history_taking: "History Taking",
  physical_examination: "Physical Examination",
  diagnostic_formulation: "Diagnostic Formulation",
  management_counselling: "Management, Counselling & Patient Education",
};

// Pick the right label map for a feedback object (new 10-domain vs legacy 7-domain).
export function domainLabelsFor(scores) {
  const keys = Object.keys(scores || {});
  const isLegacy = keys.includes("patient_centred") || keys.includes("safety_netting") || keys.includes("empathy_professionalism");
  return isLegacy ? DOMAIN_LABELS : AMC_DOMAIN_LABELS;
}

export function ratingStyle(rating) {
  switch ((rating || "").toLowerCase()) {
    case "clear pass": return "bg-[#DCFCE7] text-[#166534] border border-[#86EFAC]";
    case "pass": return "bg-[#DCFCE7] text-[#166534] border border-[#86EFAC]";
    case "clear fail": return "bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5]";
    case "fail": return "bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5]";
    default: return "bg-[#FDE7C7] text-[#92400E] border border-[#FBBF24]";
  }
}


// The four official AMC Predominant Assessment Areas (V8.3) — the case-library "skill" filter.
export const ASSESSMENT_AREAS = [
  "History Taking",
  "Examination",
  "Diagnostic Formulation",
  "Management/Counselling/Education",
];

// Compact chip labels (the full management value is long).
export const PAA_SHORT_LABEL = {
  "History Taking": "History Taking",
  "Examination": "Examination",
  "Diagnostic Formulation": "Diagnostic Formulation",
  "Management/Counselling/Education": "Management & Counselling",
};
